/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { createContext, type ReactNode, useContext, useMemo } from 'react';

import { jsx } from '@atlaskit/css';

/**
 * Configuration for Rovo Skills features.
 * This type is designed to be extensible for future configuration needs.
 */
export type RovoSkillsConfig = {
	/**
	 * Tags identifying sets of skills and conversation actions.
	 * Used to fetch skill metadata from the backend.
	 */
	tags?: {
		skillRegistryTags?: string[];
	};
};

/**
 * Context for Rovo Skills configuration.
 */
const RovoSkillsConfigContext = createContext<RovoSkillsConfig | undefined>(undefined);

/**
 * Hook to access Rovo Skills configuration.
 *
 * @returns The Rovo Skills configuration, or undefined if used outside of RovoSkillsConfigProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const config = useRovoSkillsConfig();
 *   const tags = config?.tags;
 * }
 * ```
 */
export function useRovoSkillsConfig(): RovoSkillsConfig | undefined {
	return useContext(RovoSkillsConfigContext);
}

type RovoSkillsConfigProviderProps = {
	/**
	 * Configuration for Rovo Skills features.
	 */
	config: RovoSkillsConfig;

	/**
	 * Child components that will have access to the configuration.
	 */
	children: ReactNode;
};

/**
 * Provider component that supplies Rovo Skills configuration to descendant components.
 *
 * This provider should be placed high in the component tree (e.g., at the AISmartButton level)
 * to make configuration available to all skill-related components.
 *
 * @example
 * ```tsx
 * <RovoSkillsConfigProvider config={{ tags }}>
 *   <YourComponents />
 * </RovoSkillsConfigProvider>
 * ```
 */
export function RovoSkillsConfigProvider({
	config,
	children,
}: RovoSkillsConfigProviderProps): ReactNode {
	const value = useMemo(() => config, [config]);

	return (
		<RovoSkillsConfigContext.Provider value={value}>{children}</RovoSkillsConfigContext.Provider>
	);
}
